import React, { useCallback, useState } from 'react';

import { visitIQAiSessionLimitDismissal } from '~/constants/localStorage';
import { useAiCreditsUsageQuery } from '~/types/api.graphql';

import SessionLimitInfoModal from '../SessionLimitInfoModal';
import SessionLimitBanner from './SessionLimitBanner';
import {
  buildDismissal,
  parseDismissal,
  SESSION_LIMIT_REACHED_THRESHOLD,
  SessionLimitDismissal,
  shouldShowHighUsageBanner,
} from './sessionLimitDismissal';

// Owns everything the sidebar needs for the AI session-limit UI: usage query,
// per-period dismissal state, the warning/limit banner node, and the info modal.
export const useSessionLimitBanner = (isOpen: boolean) => {
  const { data, refetch: refetchCreditsUsageRaw } = useAiCreditsUsageQuery({
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const refetchCreditsUsage = useCallback(async () => {
    if (!isOpen) return;
    try {
      await refetchCreditsUsageRaw();
    } catch {
      //
    }
  }, [isOpen, refetchCreditsUsageRaw]);

  const creditsUsage = data?.aiCreditsUsage;
  const isSessionLimitReached = !!creditsUsage?.enabled && creditsUsage.percentage >= SESSION_LIMIT_REACHED_THRESHOLD;

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [bannerDismissal, setBannerDismissal] = useState<SessionLimitDismissal | null>(() => {
    try {
      return parseDismissal(localStorage.getItem(visitIQAiSessionLimitDismissal));
    } catch {
      return null;
    }
  });

  const handleDismissBanner = useCallback(() => {
    if (!creditsUsage?.enabled) return;
    const dismissal = buildDismissal(creditsUsage.periodEnd, creditsUsage.percentage);
    try {
      localStorage.setItem(visitIQAiSessionLimitDismissal, JSON.stringify(dismissal));
    } catch {
      //
    }
    setBannerDismissal(dismissal);
  }, [creditsUsage?.enabled, creditsUsage?.periodEnd, creditsUsage?.percentage]);

  const showBlueBanner =
    !!creditsUsage?.enabled &&
    shouldShowHighUsageBanner(creditsUsage.percentage, creditsUsage.periodEnd, bannerDismissal);

  const bannerNode =
    creditsUsage && (isSessionLimitReached || showBlueBanner) ? (
      <SessionLimitBanner
        usage={creditsUsage}
        onLearnMore={() => setInfoModalOpen(true)}
        onDismiss={handleDismissBanner}
      />
    ) : null;

  const infoModalNode = <SessionLimitInfoModal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} />;

  return { isSessionLimitReached, refetchCreditsUsage, bannerNode, infoModalNode };
};
